/**
 * Best-effort server-side revocation of the Cognito refresh token, called
 * immediately before the hosted-UI logout redirect.
 *
 * The redirect to Cognito's `/logout` is what ends the SSO session, but it can
 * be rejected with a 403 when the hosted-UI cookie jar is oversized — and once
 * the browser has navigated away we have no second chance. Revoking through our
 * own API first guarantees the refresh token is invalidated either way.
 *
 * See `docs/tech/ADRs/ADR-0003-cognito-silent-signin.md` and
 * `backend/src/features/auth/tokenRevocation.ts`.
 */

import { ConfigService } from '@/services/ConfigService'

/**
 * Asks the backend to revoke the given refresh token.
 *
 * Never throws and never rejects — a failed revoke must not block sign-out.
 *
 * @param refreshToken - The current refresh token, if the session has one
 */
export async function revokeSession(refreshToken: string | undefined): Promise<void> {
  if (!refreshToken) {
    return
  }

  try {
    await fetch(`${ConfigService.getApiBaseUrl()}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
  } catch (error) {
    // Sign-out proceeds regardless; the hosted-UI logout is still ahead.
    console.warn('Refresh-token revocation failed; continuing sign-out', error)
  }
}
